import React from "react";
import {
  Box,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Button,
  Grid,
  SelectChangeEvent,
  Fade,
} from "@mui/material";
import { Search, Sort, FilterList, Close, Layers } from "@mui/icons-material";
import { storeCategoryConfigs } from "../../utils/storeHelpers";
import { StoreCategory } from "../../types/stores";

interface StoresFilterBarProps {
  showFilterBar: boolean;
  category: StoreCategory | "all";
  floor: string;
  sortBy: "name" | "floor" | "offers" | "newest";
  searchQuery: string;
  hasFilters: boolean;
  availableFloors: string[];
  onCategoryChange: (category: StoreCategory | "all") => void;
  onFloorChange: (event: SelectChangeEvent) => void;
  onSortChange: (event: SelectChangeEvent) => void;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onResetFilters: () => void;
  onClearSearch: () => void;
}

/**
 * StoresFilterBar Component
 *
 * Filter section with search input, category filter, floor filter,
 * sort dropdown, and reset filters button
 */
const StoresFilterBar: React.FC<StoresFilterBarProps> = ({
  showFilterBar,
  category,
  floor,
  sortBy,
  searchQuery,
  hasFilters,
  availableFloors,
  onCategoryChange,
  onFloorChange,
  onSortChange,
  onSearchChange,
  onResetFilters,
  onClearSearch,
}) => {
  return (
    <Fade in={showFilterBar}>
      <Box
        sx={{
          mb: 4,
          p: { xs: 1.5, sm: 3 },
          background: "white",
          borderRadius: 3,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search stores..."
              value={searchQuery}
              onChange={onSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      sx={{
                        color: "primary.main",
                        fontSize: { xs: 20, sm: 22 },
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={onClearSearch}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                          transform: "rotate(90deg)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "background.default",
                  borderRadius: 2,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  transition: "all 0.3s ease",
                  "& fieldset": { border: "none" },
                  "&:hover": {
                    bgcolor: "white",
                  },
                  "&.Mui-focused": {
                    bgcolor: "white",
                    boxShadow: "0 2px 12px rgba(26, 35, 126, 0.08)",
                    "& .MuiInputAdornment-root svg": {
                      transform: "scale(1.1)",
                    },
                  },
                },
              }}
            />
          </Grid>

          {/* Category Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Select
                value={category}
                onChange={(e) =>
                  onCategoryChange(e.target.value as StoreCategory | "all")
                }
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList
                      sx={{
                        color: "primary.main",
                        fontSize: { xs: 18, sm: 20 },
                      }}
                    />
                  </InputAdornment>
                }
                sx={{
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "& fieldset": { border: "none" },
                }}
              >
                {Object.values(storeCategoryConfigs).map((config) => (
                  <MenuItem key={config.key} value={config.key}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Floor Filter */}
          {availableFloors.length > 0 && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={floor}
                  onChange={onFloorChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <Layers
                        sx={{
                          color: "primary.main",
                          fontSize: { xs: 18, sm: 20 },
                        }}
                      />
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "grey.50",
                    borderRadius: 2,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    "& fieldset": { border: "none" },
                  }}
                >
                  <MenuItem value="all">All Floors</MenuItem>
                  {availableFloors.map((floorOption) => (
                    <MenuItem key={floorOption} value={floorOption}>
                      {floorOption}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Sort */}
          <Grid item xs={12} sm={12} md={3}>
            <FormControl fullWidth size="small">
              <Select
                value={sortBy}
                onChange={onSortChange}
                startAdornment={
                  <InputAdornment position="start">
                    <Sort
                      sx={{
                        color: "primary.main",
                        fontSize: { xs: 18, sm: 20 },
                      }}
                    />
                  </InputAdornment>
                }
                sx={{
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "& fieldset": { border: "none" },
                }}
              >
                <MenuItem value="name">Sort by Name</MenuItem>
                <MenuItem value="floor">Sort by Floor</MenuItem>
                <MenuItem value="offers">Most Offers</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Reset Filters */}
          {hasFilters && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onResetFilters}
                startIcon={<Close />}
                size="small"
                sx={{
                  borderRadius: 2,
                }}
              >
                Reset Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Fade>
  );
};

export default StoresFilterBar;
